import React, { useEffect } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  /** Seção do menu. Itens consecutivos com o mesmo group ficam sob um título só. */
  group: string;
}

interface SidebarProps {
  user: any; // We will use correct type User but since it's from auth context we can use Record<string, unknown> or User if imported
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  menuItems: MenuItem[];
  activeTab: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  sidebarOpen,
  setSidebarOpen,
  isSidebarCollapsed,
  setSidebarCollapsed,
  menuItems,
  activeTab
}) => {
  const navigate = useNavigate();
  // A gaveta do celular abre sempre com os nomes: o botão de recolher só existe
  // no desktop, então "recolhido" lá deixava o menu só com ícones e sem saída.
  const collapsed = isSidebarCollapsed && !sidebarOpen;

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen, setSidebarOpen]);

  return (
    <>
      <div className={`print:hidden fixed inset-y-0 left-0 z-40 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md shadow-2xl transform transition-all duration-300 ease-in-out ${collapsed ? 'w-20' : 'w-72'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 border-r border-white/20 dark:border-gray-700/50`}>
        <div className="flex flex-col h-full pt-20">
          {/* User Info */}
          <div className={`px-4 py-6 border-b border-gray-200/50 dark:border-gray-700/50 transition-all duration-300 ${collapsed ? 'px-2' : 'px-6'}`}>
            <div className={`flex items-center space-x-4 ${collapsed ? 'justify-center' : ''}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                {user?.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-12 h-12 rounded-xl object-cover"
                  />
                ) : (
                  <span className="text-white font-semibold">
                    {user?.name?.charAt(0)}
                  </span>
                )}
              </div>
              <div className={`min-w-0 transition-opacity duration-200 ${collapsed ? 'opacity-0 hidden' : 'opacity-100'}`}>
                <h3 className="font-semibold text-gray-800 dark:text-white truncate">{user?.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate" title={user?.email}>{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label="Menu principal" className="flex-1 px-4 py-6 overflow-y-auto custom-scrollbar">
            <ul className="space-y-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                const startsGroup = index === 0 || menuItems[index - 1].group !== item.group;
                return (
                  <li key={item.id}>
                    {startsGroup && (collapsed ? (
                      index > 0 && <div className="mx-2 mt-4 mb-2 border-t border-gray-200/70 dark:border-gray-700/60" />
                    ) : (
                      <p className={`px-4 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 ${index > 0 ? 'mt-5' : ''}`}>
                        {item.group}
                      </p>
                    ))}
                    <button
                      title={collapsed ? item.label : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => {
                        const route = item.id === 'dashboard' ? '' : item.id;
                        navigate(`/${route}`);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-3 text-left rounded-xl transition-all duration-200 group ${isActive
                        ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 text-blue-700 dark:text-blue-300 shadow-md border border-blue-200/50 dark:border-blue-700/30'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm'
                        } ${collapsed ? 'justify-center' : ''}`}
                    >
                      <div className={`p-2 rounded-lg transition-colors ${collapsed ? 'mr-0' : 'mr-3'} ${isActive
                        ? 'bg-white dark:bg-gray-800 shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-white dark:group-hover:bg-gray-700 group-hover:shadow-sm'
                        }`}>
                        <Icon size={18} className={isActive ? item.color : 'text-gray-500'} />
                      </div>
                      <span className={`font-medium transition-all duration-200 whitespace-nowrap ${collapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>{item.label}</span>
                      {isActive && !collapsed && (
                        <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Collapse Button */}
          <div className="px-4 py-6 border-t border-gray-200/50 dark:border-gray-700/50">
            <button
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
              className={`w-full hidden lg:flex items-center px-4 py-3 text-left rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm transition-all duration-200 group ${collapsed ? 'justify-center' : ''}`}
            >
              <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:bg-white dark:group-hover:bg-gray-700 group-hover:shadow-sm transition-colors ${collapsed ? 'mr-0' : 'mr-3'}`}>
                {collapsed ? <ChevronsRight size={18} className="text-gray-500 dark:text-gray-400" /> : <ChevronsLeft size={18} className="text-gray-500 dark:text-gray-400" />}
              </div>
              <span className={`font-medium transition-all duration-200 whitespace-nowrap ${collapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
                {collapsed ? 'Expandir' : 'Recolher'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
};
